import React, { useState } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { masterEventService } from '../../services/masterEvent';
import TeamsPanel from '../shared/TeamsPanel';
import TeamTag from '../shared/TeamTag';
import { teamsService } from '../../services/teams';
import { scoringService } from '../../services/scoring';
import { authService } from '../../services/auth';
import Avatar from '../shared/Avatar';
import ActivityForm from '../shared/ActivityForm';
import { isActivityObfuscated } from '../../services/activityVisibility';
import { isCompetitionActivity } from '../../services/activityTraits';
import HelpTip from '../shared/HelpTip';
import { ROLE_OPTIONS } from '../../services/roles';
import './MasterEventAdmin.css';

const TABS = ['Members', 'Activities', 'Teams', 'Scoring'];

function MembersTab({ masterEvent, members, refreshData }) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [error, setError] = useState('');

  const handleRoleChange = async (userId, newRole) => {
    try {
      await masterEventService.updateMemberRole(masterEvent.id, userId, newRole);
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'read-only' : 'active';
    try {
      await masterEventService.updateMemberStatus(masterEvent.id, userId, newStatus);
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEnabledToggle = (userId) => {
    const user = authService.getAllUsers().find((u) => u.id === userId);
    if (!user) return;
    try {
      authService.setEnabled(userId, !user.enabled);
      refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await masterEventService.removeMember(masterEvent.id, userId);
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteExisting, setInviteExisting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const signUpUrl = window.location.origin + '/login';

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setInviteSuccess(null);
    setInviteExisting(false);
    if (!inviteEmail.trim()) { setError('Email is required'); return; }
    try {
      const result = await masterEventService.inviteByEmail(masterEvent.id, inviteEmail.trim(), inviteName.trim() || undefined);
      setInviteSuccess(inviteEmail.trim());
      setInviteExisting(result?.existing || false);
      setInviteEmail('');
      setInviteName('');
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(signUpUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  return (
    <div className="mea-tab-content">
      {error && <div className="mea-error">{error}</div>}

      <div className="mea-section">
        <h4 className="mea-section-title">Invite Member</h4>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px' }}>
          Reserve a spot by email. They need to sign up or use Google with the same email to join.
        </p>
        <form className="mea-invite-form" onSubmit={handleInvite}>
          <input
            type="email"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="mea-input"
          />
          <input
            type="text"
            placeholder="Display name (optional)"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            className="mea-input"
          />
          <button type="submit" className="mea-btn mea-btn--primary">Reserve Spot</button>
        </form>

        {inviteSuccess && (
          <div style={{
            marginTop: 12, padding: 14, background: inviteExisting ? 'rgba(59,130,246,0.08)' : 'rgba(34,197,94,0.08)',
            border: `1px solid ${inviteExisting ? 'rgba(59,130,246,0.2)' : 'rgba(34,197,94,0.2)'}`, borderRadius: 10,
          }}>
            <div style={{ fontSize: 13, color: inviteExisting ? '#60a5fa' : '#22c55e', fontWeight: 600, marginBottom: 8 }}>
              {inviteExisting
                ? `${inviteSuccess} is an existing user — added directly to this event.`
                : `Spot reserved for ${inviteSuccess}`}
            </div>
            {!inviteExisting && (
              <>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', margin: '0 0 8px', lineHeight: 1.5 }}>
                  Share this link with them. Once they sign up with <strong style={{ color: '#fff' }}>{inviteSuccess}</strong> (or Google with the same email), they'll automatically join this event.
                </p>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="text" readOnly value={signUpUrl}
                    style={{ flex: 1, padding: '7px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#60a5fa', fontSize: 12, outline: 'none' }}
                    onClick={(e) => e.target.select()} />
                  <button onClick={handleCopyLink} style={{
                    padding: '7px 12px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)',
                    borderRadius: 6, color: '#60a5fa', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                    {copiedLink ? '✓ Copied' : 'Copy Link'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mea-section">
        <h4 className="mea-section-title">Members ({members.length})</h4>
        <div className="mea-members-list">
          {members.map((mem) => {
            const fullUser = authService.getAllUsers().find((u) =>
              u.id === mem.userId ||
              u.userId === mem.userId ||
              u.cognitoId === mem.userId ||
              u.id === mem.user?.id
            );
            const isOwner = mem.role === 'owner';
            const isEnabled = fullUser?.enabled !== false;
            const isPending = fullUser && !fullUser.cognitoId;
            return (
              <div key={mem.id} className="mea-member-row" style={isPending ? { opacity: 0.7 } : undefined}>
                <Avatar user={mem.user} size={18} className="mea-member-avatar" />
                <div className="mea-member-info">
                  <span className="mea-member-name">
                    {mem.user?.displayName || 'Unknown'}<TeamTag user={mem.user} userId={mem.userId} />
                    {isPending && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 8,
                        background: 'rgba(245,158,11,0.12)', color: '#fbbf24', fontWeight: 700,
                      }}>PENDING</span>
                    )}
                  </span>
                  <span className="mea-member-email" style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', display: 'block' }}>
                    {fullUser?.email || mem.user?.email || ''}
                    {isPending && <span style={{ color: 'rgba(245,158,11,0.6)', marginLeft: 4 }}>— hasn't signed up yet</span>}
                  </span>
                </div>
                <select
                  className="mea-role-select"
                  value={mem.role}
                  onChange={(e) => handleRoleChange(mem.userId, e.target.value)}
                  disabled={isOwner}
                >
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button
                  className={`mea-btn mea-btn--small ${mem.status === 'active' ? 'mea-btn--active' : 'mea-btn--readonly'}`}
                  onClick={() => handleStatusToggle(mem.userId, mem.status)}
                >
                  {mem.status}
                </button>
                {!isOwner && (
                  <button
                    className={`mea-btn mea-btn--small ${isEnabled ? 'mea-btn--active' : 'mea-btn--danger'}`}
                    onClick={() => handleEnabledToggle(mem.userId)}
                    title={isEnabled ? 'Click to disable account' : 'Click to enable account'}
                    style={{
                      minWidth: 80,
                      background: isEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                      color: isEnabled ? '#22c55e' : '#ef4444',
                      border: `1px solid ${isEnabled ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    }}
                  >
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </button>
                )}
                {!isOwner && (
                  <button
                    className="mea-btn mea-btn--small mea-btn--danger"
                    onClick={() => handleRemove(mem.userId)}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ActivitiesTab({ masterEvent, activities, refreshData }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [savingVisibilityId, setSavingVisibilityId] = useState(null);

  const handleCreate = async (data) => {
    setError('');
    try {
      await masterEventService.createActivity({ masterEventId: masterEvent.id, ...data });
      setShowCreate(false);
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const handleEdit = async (actId, data) => {
    setError('');
    try {
      await masterEventService.updateActivity(actId, data);
      setEditingId(null);
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (actId) => {
    if (!window.confirm('Delete this activity? This cannot be undone.')) return;
    setError('');
    try {
      await masterEventService.deleteActivity(actId);
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const handleConclude = async (actId) => {
    if (!window.confirm('Conclude this activity?')) return;
    setError('');
    try {
      await masterEventService.concludeActivity(actId);
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const TYPE_ICONS = { teamselection: '👥', betting: '🎯', openbets: '🤝', food: '🍽️', game: '🎮', quiz: '🧠', vote: '🗳️', transport: '🚀', custom: '📋' };

  const handleToggleVisibility = async (act) => {
    setError('');
    setSavingVisibilityId(act.id);
    try {
      await masterEventService.updateActivity(act.id, { obfuscated: !isActivityObfuscated(act, masterEvent) });
      await refreshData();
    } catch (err) { setError(err.message); }
    finally { setSavingVisibilityId(null); }
  };

  const handleToggleDraft = async (actId, currentStatus) => {
    const next = currentStatus === 'draft' ? 'active' : 'draft';
    setError('');
    try {
      await masterEventService.updateActivity(actId, { status: next });
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="mea-tab-content">
      {error && <div className="mea-error">{error}</div>}

      <div className="mea-section" style={{ marginBottom: 16 }}>
        <h4 className="mea-section-title" style={{ margin: 0 }}>Activity Visibility</h4>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0' }}>
          Obfuscate or reveal each activity independently. Members see the optional hint while it is obfuscated, and the full name and details once revealed. Admins always see the full details.
        </p>
      </div>

      <div className="mea-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h4 className="mea-section-title" style={{ margin: 0 }}>Activities ({activities.length})</h4>
          <button className="mea-btn mea-btn--primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => { setShowCreate(!showCreate); setEditingId(null); }}>
            {showCreate ? '✕ Cancel' : '+ New Activity'}
          </button>
        </div>

        {showCreate && (
          <div style={{ marginBottom: 16, padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
            <ActivityForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} submitLabel="Create Activity" />
          </div>
        )}

        {activities.length === 0 && !showCreate ? (
          <p className="mea-empty">No activities yet. Click "+ New Activity" to get started.</p>
        ) : (
          <div className="mea-activities-list">
            {activities.map((act) => (
              <div key={act.id}>
                {editingId === act.id ? (
                  <div style={{ padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(59,130,246,0.2)', marginBottom: 6 }}>
                    <ActivityForm
                      initial={act}
                      masterEvent={masterEvent}
                      onSubmit={(data) => handleEdit(act.id, data)}
                      onCancel={() => setEditingId(null)}
                      submitLabel="Save Changes"
                    />
                  </div>
                ) : (
                  <div className="mea-activity-row">
                    <span className="mea-activity-icon">
                      {act.image ? (act.image.startsWith?.('data:') ? <img src={act.image} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} /> : act.image) : (TYPE_ICONS[act.type] || '📋')}
                    </span>
                    <div className="mea-activity-info">
                      <span className="mea-activity-name">{act.name}</span>
                      <span className="mea-activity-meta">
                        {act.type}{isCompetitionActivity(act) && (act.isTeamBased ? ' · 🏆 teams' : ' · 🏆 individual')}
                        {act.date && <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.4)' }}>{new Date(act.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                      </span>
                    </div>
                    <span className={`mea-status-badge mea-status-badge--${act.status}`}>{act.status}</span>
                    <button
                      className={`mea-btn mea-btn--small ${act.status === 'draft' ? 'mea-btn--warn' : 'mea-btn--active'}`}
                      onClick={() => handleToggleDraft(act.id, act.status)}
                      title={act.status === 'draft' ? 'Publish (go live)' : 'Move back to draft'}
                      style={{ minWidth: 70 }}
                    >
                      {act.status === 'draft' ? '🚀 Publish' : '📝 Draft'}
                    </button>
                    <button
                      type="button"
                      className={`mea-btn mea-btn--small ${isActivityObfuscated(act, masterEvent) ? 'mea-btn--warn' : 'mea-btn--active'}`}
                      onClick={() => handleToggleVisibility(act)}
                      disabled={savingVisibilityId !== null}
                      title={isActivityObfuscated(act, masterEvent) ? 'Reveal activity to members' : 'Obfuscate activity for members'}
                      aria-label={`${isActivityObfuscated(act, masterEvent) ? 'Reveal' : 'Obfuscate'} ${act.name}`}
                      style={{ minWidth: 100 }}
                    >
                      {savingVisibilityId === act.id ? 'Saving...' : isActivityObfuscated(act, masterEvent) ? '🙈 Obfuscated' : '👁️ Visible'}
                    </button>
                    <button className="mea-btn mea-btn--small" onClick={() => { setEditingId(act.id); setShowCreate(false); }} title="Edit activity">✏️</button>
                    {act.status === 'active' && <button className="mea-btn mea-btn--small mea-btn--warn" onClick={() => handleConclude(act.id)}>Conclude</button>}
                    <button className="mea-btn mea-btn--small mea-btn--danger" onClick={() => handleDelete(act.id)}>Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamsTab({ activities }) {
    const [scope, setScope] = useState('');
    return <div><select className="mea-input" value={scope} onChange={e => setScope(e.target.value)}><option value="">Event teams</option>{activities.filter(a => a.isTeamBased).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select><TeamsPanel activityId={scope || null} /></div>;
  }

  function ScoringTab({ masterEvent, activities, members, scores, refreshData }) {
  const [selectedActivity, setSelectedActivity] = useState('');
  const [placements, setPlacements] = useState([]);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Score entry is for concluded competitions; keep anything already scored editable.
  const concludedActivities = activities.filter((a) => a.status === 'concluded' && (isCompetitionActivity(a) || scores.some((s) => s.activityId === a.id)));
  const currentActivity = activities.find((a) => a.id === selectedActivity);

  const handleActivitySelect = (actId) => {
    setSelectedActivity(actId);
    setSaved(false);
    setError('');
    const act = activities.find((a) => a.id === actId);
    if (act) {
      const existing = scores.filter((s) => s.activityId === actId);
      if (existing.length > 0) {
        setPlacements(existing.map((s) => ({
          placement: s.placement,
          points: s.points,
          userId: s.userId || '',
          teamId: s.teamId || '',
        })));
      } else {
        setPlacements(
          (act.pointsConfig || []).map((pc) => ({
            placement: pc.place,
            points: pc.points,
            userId: '',
            teamId: '',
          }))
        );
      }
    }
  };

  const handlePlacementChange = (idx, userId) => {
    const updated = [...placements];
    updated[idx] = { ...updated[idx], [currentActivity?.isTeamBased ? 'teamId' : 'userId']: userId };
    setPlacements(updated);
  };

  const handleSave = async () => {
    setError('');
    setSaved(false);
    if (!selectedActivity) { setError('Select an activity'); return; }

    const rankings = placements
      .filter((p) => currentActivity?.isTeamBased ? p.teamId : p.userId)
      .map((p) => ({
        userId: currentActivity?.isTeamBased ? null : p.userId,
        teamId: currentActivity?.isTeamBased ? p.teamId : null,
        placement: p.placement,
        points: p.points,
      }));

    if (rankings.length === 0) {
      setError('Assign at least one placement');
      return;
    }

    try {
      await scoringService.setScores(masterEvent.id, selectedActivity, rankings);
      setSaved(true);
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mea-tab-content">
      {error && <div className="mea-error">{error}</div>}

      {concludedActivities.length === 0 ? (
        <p className="mea-empty">No concluded competitions to score. Mark an activity as a Competition and conclude it from the Activities tab.</p>
      ) : (
        <>
          <div className="mea-section">
            <h4 className="mea-section-title">Select Concluded Activity</h4>
            <select
              className="mea-input mea-input--select"
              value={selectedActivity}
              onChange={(e) => handleActivitySelect(e.target.value)}
            >
              <option value="">Choose activity...</option>
              {concludedActivities.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
              ))}
            </select>
          </div>

          {currentActivity && placements.length > 0 && (
            <div className="mea-section">
              <h4 className="mea-section-title">Assign Placements<HelpTip label="Assigning placements">{currentActivity.isTeamBased ? 'Pick the team for each place. Teams come from Team Selection unless this activity has its own teams.' : 'Pick the player for each place.'} Points follow the activity's placement table. Saving again replaces the earlier result.</HelpTip></h4>
              <div className="mea-placements-list">
                {placements.map((p, idx) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <div key={idx} className="mea-placement-row">
                      <span className="mea-placement-medal">
                        {medals[p.placement - 1] || `#${p.placement}`}
                      </span>
                      <span className="mea-placement-points">{p.points} pts</span>
                      <select
                        className="mea-input mea-input--select"
                        value={currentActivity.isTeamBased ? p.teamId : p.userId}
                        onChange={(e) => handlePlacementChange(idx, e.target.value)}
                      >
                        <option value="">{currentActivity.isTeamBased ? 'Select team...' : 'Select player...'}</option>
                        {currentActivity.isTeamBased ? teamsService.getForActivity(currentActivity.id).map(t => <option key={t.id} value={t.id}>{t.name}</option>) : members.map((m) => (
                          <option key={m.userId} value={m.userId}>
                            {m.user?.displayName || m.userId}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <div className="mea-placement-actions">
                <button className="mea-btn mea-btn--primary" onClick={handleSave}>
                  Save Scores
                </button>
                {saved && <span className="mea-saved">Scores saved!</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MasterEventAdmin() {
  const { masterEvent, members, activities, scores, isAdmin, refreshData } = useMasterEvent();
  const [activeTab, setActiveTab] = useState('Members');

  if (!isAdmin) {
    return (
      <div className="mea">
        <div className="mea-denied">
          <h3>Access Denied</h3>
          <p>You need to be an owner or co-organizer to access admin settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mea">
      <h2 className="mea-title">Admin Panel</h2>

      <div className="mea-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`mea-tab ${activeTab === tab ? 'mea-tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Members' && (
        <MembersTab masterEvent={masterEvent} members={members} refreshData={refreshData} />
      )}
      {activeTab === 'Activities' && (
        <ActivitiesTab masterEvent={masterEvent} activities={activities} refreshData={refreshData} />
      )}
      {activeTab === 'Teams' && (
        <TeamsTab masterEvent={masterEvent} activities={activities} members={members} refreshData={refreshData} />
      )}
      {activeTab === 'Scoring' && (
        <ScoringTab masterEvent={masterEvent} activities={activities} members={members} scores={scores} refreshData={refreshData} />
      )}
    </div>
  );
}
